/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module MatchModule */

import {
    Component,
    Input,
    OnInit,
} from '@angular/core';

import { StringOperation } from 'generated-types';
import { StringMatch } from 'object-types';
import { L10nService } from '@vmw/ngx-vip';

import {
    DropdownModelSingleValue,
} from 'standalone-components/avi-dropdown/avi-dropdown.types';

import { StringMatchConfigItem }
    from 'ajs/modules/match/factories/string-match.config-item.factory';
import { IStringGroupRefsOrMatchStringsConfig } from '../../match.types';
import * as l10n from './string-match-config.l10n';
import './string-match-config.component.less';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @description
 *      StringMatch Configuration component.
 *      (cannot be used as individual match ie doesnt have match wrapper).
 *
 * @author Aravindh Nagarajan, alextsg
 */
@Component({
    selector: 'string-match-config',
    templateUrl: './string-match-config.component.html',
})
export class StringMatchConfigComponent implements OnInit {
    /**
     * StringMatchConfigItem instance.
     */
    @Input()
    public editable: StringMatchConfigItem;

    /**
     * Unique Index to generate ID.
     */
    @Input()
    public id: number | string;

    /**
     * Label for string-group-or-custom-value element.
     */
    @Input()
    public stringLabel: string;

    /**
     * If true, removes REGEX_MATCH and REGEX_DOES_NOT_MATCH options.
     */
    @Input()
    public disallowRegex = false;

    /**
     * Flag to hide or show the add item button.
     */
    @Input()
    public showAddItemButton = true;

    /**
     * Flag to track if the fields inside string-match are required.
     * For Client Insights inside the VS Modal, all the fields of this match are optional.
     */
    @Input()
    public areMatchFieldsRequired = true;

    /**
     * Get keys from source bundles for template usage.
     */
    public readonly l10nKeys = l10nKeys;

    /**
     * Object types used in template.
     */
    public readonly objectType = StringMatch;

    public stringGroupOrCustomValueLabel: string;

    /**
     * List of enums values to be hidden from the dropdown of StringOperation values.
     */
    public hiddenEnumValues: DropdownModelSingleValue[] = [];

    constructor(private readonly l10nService: L10nService) {
        l10nService.registerSourceBundles(dictionary);
    }

    /**
     * @override
     * Populates the list of stringGroupRefsOrCustomValuesConfigs with the configured refs and match
     * strings.
     */
    public ngOnInit(): void {
        if (this.disallowRegex) {
            this.hiddenEnumValues = [
                StringOperation.REGEX_DOES_NOT_MATCH,
                StringOperation.REGEX_MATCH,
            ];
        }

        this.stringGroupOrCustomValueLabel = this.stringLabel || this.l10nService.getMessage(l10nKeys.stringLabel);
    }

    /**
     * Returns an empty config object.
     */
    private getDefaultConfig(): IStringGroupRefsOrMatchStringsConfig {
        return {
            stringGroupRefs: [],
            matchStrings: [],
        };
    }
}
